import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="py-8 text-center text-[#8C7E6E] text-sm bg-[#E8E0D5]" data-id="element-36">
        <p className="mb-3" data-id="element-37">
          © {new Date().getFullYear()} The Calm Home Reset. All rights
          reserved.
        </p>
        <div className="flex justify-center gap-6" data-id="element-38">
          <button onClick={() => setActiveModal('privacy')} className="hover:text-[#4A4238] underline underline-offset-2 transition-colors" data-id="element-39">
            Privacy Policy
          </button>
          <button onClick={() => setActiveModal('terms')} className="hover:text-[#4A4238] underline underline-offset-2 transition-colors" data-id="element-40">
            Terms of Service
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-41">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeModal} data-id="element-42">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="bg-[#FAF7F2] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-43">
              <div className="flex items-center justify-between p-6 border-b border-[#E8E0D5]" data-id="element-44">
                <h2 className="text-2xl font-serif text-[#4A4238]" data-id="element-45">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-[#E8E0D5] transition-colors text-[#8C7E6E] hover:text-[#4A4238]" data-id="element-46">
                  <X size={20} data-id="element-47" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] prose prose-sm text-[#4A4238]" data-id="element-48">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-49" /> : <TermsContent data-id="element-50" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <div className="space-y-6" data-id="element-51">
      <p className="text-[#8C7E6E] italic" data-id="element-52">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-53">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-54">
          Information We Collect
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-55">
          When you purchase The Calm Home Reset, we collect only the information
          necessary to process your order, including your email address and
          payment information. Payment processing is handled securely by our
          third-party payment processor.
        </p>
      </section>

      <section data-id="element-56">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-57">
          How We Use Your Information
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-58">
          We use your email address solely to deliver your digital product and
          send important updates about your purchase. We do not sell, rent, or
          share your personal information with third parties for marketing
          purposes.
        </p>
      </section>

      <section data-id="element-59">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-60">
          Cookies
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-61">
          Our website uses essential cookies to ensure proper functionality. We
          may also use analytics cookies to understand how visitors interact
          with our site, helping us improve your experience.
        </p>
      </section>

      <section data-id="element-62">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-63">
          Your Rights
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-64">
          You have the right to access, correct, or delete your personal
          information at any time. You may also unsubscribe from our emails
          using the link provided in each message.
        </p>
      </section>

      <section data-id="element-65">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-66">
          Data Security
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-67">
          We implement appropriate security measures to protect your personal
          information against unauthorized access, alteration, or destruction.
        </p>
      </section>
    </div>;
}
function TermsContent() {
  return <div className="space-y-6" data-id="element-68">
      <p className="text-[#8C7E6E] italic" data-id="element-69">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <section data-id="element-70">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-71">
          Digital Product
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-72">
          The Calm Home Reset is a digital product delivered as a downloadable
          PDF. Upon successful payment, you will receive immediate access to
          download your copy.
        </p>
      </section>

      <section data-id="element-73">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-74">
          License & Usage
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-75">
          Your purchase grants you a personal, non-transferable license to use
          The Calm Home Reset for your own personal use. You may not
          redistribute, resell, or share the content with others.
        </p>
      </section>

      <section data-id="element-76">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-77">
          Refund Policy
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-78">
          Due to the digital nature of this product, all sales are final.
          However, if you experience technical issues accessing your purchase,
          please reach out and we'll do our best to help.
        </p>
      </section>

      <section data-id="element-79">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-80">
          Disclaimer
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-81">
          The Calm Home Reset is designed to provide general guidance and
          support for postpartum organization. It is not a substitute for
          professional medical, mental health, or childcare advice. Always
          consult qualified professionals for specific concerns.
        </p>
      </section>

      <section data-id="element-82">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-83">
          Intellectual Property
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-84">
          All content, design, and materials in The Calm Home Reset are
          protected by copyright. Unauthorized reproduction or distribution is
          prohibited.
        </p>
      </section>

      <section data-id="element-85">
        <h3 className="text-lg font-serif font-medium text-[#4A4238] mb-2" data-id="element-86">
          Changes to Terms
        </h3>
        <p className="text-[#8C7E6E] leading-relaxed" data-id="element-87">
          We reserve the right to update these terms at any time. Continued use
          of our products after changes constitutes acceptance of the new terms.
        </p>
      </section>
    </div>;
}